import { FlightPax, FlightProduct, MandatoryField } from '@/api/Cart/cart.interfaces';
import { Validation } from '@ui/components/FormV2/Validations/Validations.interfaces';
import {
    isDefinedObject,
    isName,
    isRequired,
    isSelected,
} from '@ui/components/FormV2/Validations/functions';
import { getAgeRange, getProductToDate } from '@/api/Cart/cart.helpers';
import { AgeRangeDefinitions } from '@/api/Checkout/checkout.interfaces';
import { isNotNil } from '@shared/utils/value';
import { isValidDateOfBirth } from '@/helpers/checkout';

interface Params {
    pax: FlightPax;
    index: number;
    ageRangeDefinitions: AgeRangeDefinitions;
    toDate: Date;
    product: FlightProduct;
}

interface FormField {
    name: string;
    validation: Validation[];
    field: MandatoryField;
}

export const getProductPaxFields = (
    product: FlightProduct,
    ageRangeDefinitions: AgeRangeDefinitions
): FormField[] => {
    const toDate = getProductToDate(product) ?? new Date();
    return product.Pax.map((pax, index) =>
        getPaxFields({
            pax,
            index,
            ageRangeDefinitions,
            toDate,
            product,
        })
    ).flat();
};

export const getPaxFields = (params: Params): FormField[] => {
    const fields = [
        MandatoryField.Gender,
        MandatoryField.FirstName,
        MandatoryField.LastName,
        MandatoryField.DateOfBirth,
        MandatoryField.Nationality,
    ];
    return fields.map((field) => getPaxField(field, params)).filter(isNotNil);
};

const getPaxField = (field: MandatoryField, params: Params): FormField | undefined => {
    const { pax, index } = params;
    return pax.MandatoryFields.includes(field)
        ? {
              field,
              name: `${field}${index}`,
              validation: getFieldValidation(field, params),
          }
        : undefined;
};

const getFieldValidation = (field: MandatoryField, params: Params): Validation[] => {
    switch (field) {
        case MandatoryField.Gender:
            return [isSelected];
        case MandatoryField.FirstName:
        case MandatoryField.LastName:
            return [isRequired, isName];
        case MandatoryField.Nationality:
            return [isDefinedObject];
        case MandatoryField.DateOfBirth: {
            const ageRange = getAgeRange(
                params.product,
                params.pax,
                params.ageRangeDefinitions
            );

            return params.toDate
                ? [(date) => isValidDateOfBirth(date, ageRange, params.toDate)]
                : [];
        }
        default:
            return [];
    }
};
